import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import { usePincodeLookup } from "../lib/usePincodeLookup";
import FileUpload from "../components/FileUpload";
import NccBrandedHeader from "../components/NccBrandedHeader";

function computeAge(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? String(age) : "";
}

export default function PayrollEmployeePage() {
  const [trades, setTrades] = useState([]);
  const [nccSites, setNccSites] = useState([]);
  const [idProofTypes, setIdProofTypes] = useState(["PAN", "Aadhar"]);
  const [form, setForm] = useState({
    name: "", contact_phone: "", contact_email: "",
    village: "", pincode: "", city: "", state: "",
    trade: "",
    experience_india_years: 0,
    experience_abroad_years: 0,
    current_status: "NEW",
    employee_id: "",
    uan_no: "",
    ncc_site_name: "",
    id_proof_type: "Aadhar",
    id_proof_number: "",
    referred_by: "", referred_by_name: "", referred_by_phone: "",
    date_of_birth: "",
    bio: "",
    photo: null, id_docs: [], certifications: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  // Inline duplicate checks (mirror RegisterPage): status = idle | checking | available | taken
  const [dup, setDup] = useState({ phone: "idle", email: "idle", id_proof: "idle" });
  const dupTimers = useRef({});
  const hasDuplicates = dup.phone === "taken" || dup.email === "taken" || dup.id_proof === "taken";

  const { pinLoading, pinSuggestions, onPincodeChange } = usePincodeLookup(({ pincode, city, state }) => {
    setForm((f) => ({
      ...f,
      pincode: pincode ?? f.pincode,
      city: f.city || city || "",
      state: f.state || state || "",
    }));
  });

  function checkDuplicate(field, value) {
    if (dupTimers.current[field]) clearTimeout(dupTimers.current[field]);
    if (!value || !value.trim()) {
      setDup((d) => ({ ...d, [field]: "idle" }));
      return;
    }
    setDup((d) => ({ ...d, [field]: "checking" }));
    dupTimers.current[field] = setTimeout(async () => {
      try {
        const r = await api.get("/contractors/check-duplicate", { params: { field, value } });
        setDup((d) => ({ ...d, [field]: r.data.available ? "available" : "taken" }));
      } catch {
        setDup((d) => ({ ...d, [field]: "idle" }));
      }
    }, 450);
  }

  useEffect(() => {
    api.get("/meta/trades").then((r) => {
      setTrades(r.data.trades || []);
      setNccSites(r.data.ncc_sites || []);
      const ipt = r.data.id_proof_types || [];
      if (ipt.length > 0) setIdProofTypes(ipt);
    }).catch(() => {});
  }, []);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.trade) { setError("Please select a trade"); return; }
    if (!form.date_of_birth) { setError("Date of Birth is required"); return; }
    if (!form.id_proof_number || form.id_proof_number.replace(/\D/g, "").length !== 12) {
      setError("Aadhaar No. is required (12 digits)");
      return;
    }
    if (form.current_status === "NCCL") {
      if (!form.employee_id.trim()) { setError("Employee ID is required for NCCL staff"); return; }
      if (!form.ncc_site_name.trim()) { setError("NCC Site Name is required for NCCL staff"); return; }
    }
    setSubmitting(true);
    try {
      const body = {
        name: form.name,
        contact_phone: form.contact_phone,
        contact_email: form.contact_email || null,
        village: form.village || null,
        pincode: form.pincode || null,
        city: form.city,
        state: form.state,
        location: [form.city, form.state].filter(Boolean).join(", "),
        contractor_type: "Payroll Employee",
        current_status: form.current_status,
        ncc_site_name: form.ncc_site_name || null,
        id_proof_type: form.id_proof_type,
        id_proof_number: form.id_proof_number || null,
        bio: form.bio,
        trades: [form.trade],
        trade_counts: { [form.trade]: 1 },
        crew_size: 1,
        experience_years: Number(form.experience_india_years || 0) + Number(form.experience_abroad_years || 0),
        is_payroll: true,
        employee_id: form.employee_id || null,
        uan_no: form.uan_no || null,
        experience_india_years: Number(form.experience_india_years || 0),
        experience_abroad_years: Number(form.experience_abroad_years || 0),
        referred_by: form.referred_by || null,
        referred_by_name: form.referred_by ? (form.referred_by_name || null) : null,
        referred_by_phone: form.referred_by ? (form.referred_by_phone || null) : null,
        date_of_birth: form.date_of_birth || null,
        age_years: form.date_of_birth ? Number(computeAge(form.date_of_birth) || 0) || null : null,
        photo: form.photo,
        id_docs: form.id_docs,
        certifications: form.certifications,
        projects: [],
      };
      await api.post("/contractors", body);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 md:px-8">
        <NccBrandedHeader subtitle="Registration submitted" tagline="Your profile is pending verification by HR / Admin." />
        <div className="mt-8 rounded-2xl border border-emerald-300 bg-emerald-50 p-8 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
          <h2 className="mt-3 font-heading text-2xl font-extrabold text-emerald-900">Thanks, {form.name}!</h2>
          <p className="mt-2 max-w-lg mx-auto text-sm text-slate-700">Your profile is now under review. You&apos;ll be notified once it&apos;s approved and visible to the team.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#1e3a8a] px-5 py-2.5 text-sm font-bold tracking-wider text-white">BACK TO LOGIN</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 md:px-8">
      <NccBrandedHeader subtitle="New Payroll Manpower" tagline="Fill in your details below." />

      <form onSubmit={submit} className="mt-8 space-y-8" data-testid="payroll-form">
        <Section title="Basic Information">
          <Grid>
            <TField label="Full Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="pe-name" />
            <TField label="Phone" required value={form.contact_phone}
              onChange={(v) => { setForm({ ...form, contact_phone: v }); checkDuplicate("phone", v); }}
              testid="pe-phone"
              status={dup.phone}
              statusMessage="This phone number is already registered. Please use a different one." />
            <label className="block">
              <Label>Pincode</Label>
              <div className="relative">
                <input
                  value={form.pincode}
                  onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setForm((f) => ({ ...f, pincode: v })); onPincodeChange(v); }}
                  placeholder="6 digits"
                  data-testid="pe-pincode"
                  className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 pr-9 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
                {pinLoading && <Loader2 className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
              </div>
              {pinSuggestions.length > 0 && (
                <div className="mt-1 font-mono text-[10px] text-slate-500">
                  {pinSuggestions.length} post offices found · use Village dropdown to pick
                </div>
              )}
            </label>
            <label className="block">
              <Label>Village</Label>
              <input
                list="pe-village-suggestions"
                value={form.village}
                onChange={(e) => setForm({ ...form, village: e.target.value })}
                placeholder={pinSuggestions.length ? "Pick or type…" : ""}
                data-testid="pe-village"
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
              />
              <datalist id="pe-village-suggestions">
                {pinSuggestions.map((p) => <option key={p} value={p} />)}
              </datalist>
            </label>
            <TField label="City" required value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="pe-city" />
            <TField label="State" required value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="pe-state" />
            <TField label="Email" type="email" value={form.contact_email}
              onChange={(v) => { setForm({ ...form, contact_email: v }); checkDuplicate("email", v); }}
              testid="pe-email"
              status={dup.email}
              statusMessage="This email is already registered. Please use a different one." />
            <label className="block">
              <Label>UAN No. <span className="font-mono text-xs text-slate-500">(optional)</span></Label>
              <input
                value={form.uan_no}
                onChange={(e) => setForm({ ...form, uan_no: e.target.value.replace(/\D/g, "").slice(0, 12) })}
                placeholder="12-digit UAN"
                inputMode="numeric"
                data-testid="pe-uan"
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm tracking-wider focus:border-slate-900 focus:outline-none"
              />
            </label>
            <div>
              <Label>Date of Birth <span className="text-safety-600">*</span></Label>
              <input
                required
                type="date"
                value={form.date_of_birth}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                data-testid="pe-dob"
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
              />
            </div>
            <div>
              <Label>Age (Yrs)</Label>
              <input
                type="text"
                readOnly
                value={computeAge(form.date_of_birth)}
                data-testid="pe-age"
                placeholder="Auto-calculated"
                className="w-full cursor-not-allowed border-2 border-slate-200 bg-slate-100 px-3 py-2 font-mono text-sm text-slate-700"
              />
            </div>
          </Grid>

          <div className="mt-4">
            <Label>Type of Trade <span className="text-safety-600">*</span></Label>
            <select required value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })}
              data-testid="pe-trade"
              className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none">
              <option value="">— Select trade —</option>
              {trades.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="mt-4">
            <Label>Experience (years)</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="industrial-label text-slate-500 mb-1">India</div>
                <input type="number" min="0" value={form.experience_india_years}
                  onChange={(e) => setForm({ ...form, experience_india_years: e.target.value })}
                  data-testid="pe-exp-india"
                  className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none" />
              </div>
              <div>
                <div className="industrial-label text-slate-500 mb-1">Abroad</div>
                <input type="number" min="0" value={form.experience_abroad_years}
                  onChange={(e) => setForm({ ...form, experience_abroad_years: e.target.value })}
                  data-testid="pe-exp-abroad"
                  className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none" />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <Label>Current Status <span className="text-safety-600">*</span></Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "NCCL", label: "NCCL", hint: "Currently engaged with NCC" },
                { id: "NEW", label: "NEW", hint: "Fresh registration" },
              ].map((s) => {
                const on = form.current_status === s.id;
                return (
                  <button key={s.id} type="button" onClick={() => setForm({ ...form, current_status: s.id })}
                    data-testid={`pe-status-${s.id}`}
                    className={`flex items-center gap-3 border-2 border-slate-900 p-3 text-left transition-all ${on ? "bg-safety-500 shadow-brutal-sm -translate-y-0.5" : "bg-white"}`}>
                    <span className={`grid h-4 w-4 place-items-center rounded-full border-2 border-slate-900 ${on ? "bg-slate-900" : "bg-white"}`}>
                      {on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </span>
                    <span>
                      <div className="industrial-label">{s.label}</div>
                      <div className="font-mono text-xs text-slate-700">{s.hint}</div>
                    </span>
                  </button>
                );
              })}
            </div>
            {form.current_status === "NCCL" && (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Employee ID No. <span className="text-safety-600">*</span></Label>
                  <input required value={form.employee_id}
                    onChange={(e) => setForm({ ...form, employee_id: e.target.value.toUpperCase() })}
                    placeholder="e.g. NCC-12345"
                    data-testid="pe-employee-id"
                    className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm tracking-wider focus:border-slate-900 focus:outline-none" />
                </div>
                <div>
                  <Label>NCC Site Name <span className="text-safety-600">*</span></Label>
                  <input required list="pe-ncc-sites" value={form.ncc_site_name}
                    onChange={(e) => setForm({ ...form, ncc_site_name: e.target.value })}
                    placeholder={nccSites.length > 0 ? "Select or type a site…" : "NCC Site"}
                    data-testid="pe-ncc-site"
                    className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none" />
                  <datalist id="pe-ncc-sites">
                    {nccSites.map((s) => <option key={s} value={s} />)}
                  </datalist>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4">
            <Label>Aadhar No. <span className="text-safety-600">*</span></Label>
            <input required value={form.id_proof_number}
              onChange={(e) => { const v = e.target.value.toUpperCase(); setForm({ ...form, id_proof_number: v, id_proof_type: "Aadhar" }); checkDuplicate("id_proof", v); }}
              placeholder="1234 5678 9012"
              maxLength={14}
              data-testid="pe-id-number"
              className={`w-full border-2 bg-slate-50 px-3 py-2 font-mono text-sm tracking-wider focus:outline-none ${
                dup.id_proof === "taken" ? "border-red-500 focus:border-red-600"
                : dup.id_proof === "available" ? "border-emerald-500 focus:border-emerald-600"
                : "border-slate-300 focus:border-slate-900"
              }`} />
            {dup.id_proof === "checking" && (
              <div className="mt-1 inline-flex items-center gap-1 font-mono text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" /> checking…
              </div>
            )}
            {dup.id_proof === "taken" && (
              <div className="mt-1 font-mono text-xs text-red-600" data-testid="pe-id-number-taken-msg">
                This Aadhar is already registered. Please verify and try again.
              </div>
            )}
            <div className="mt-1 font-mono text-xs text-slate-500">
              12 digits · e.g. 1234 5678 9012
            </div>
          </div>

          <div className="mt-4" data-testid="pe-referred-by-wrapper">
            <Label>Referred by <span className="font-mono text-xs text-slate-500">(optional)</span></Label>
            <select
              value={form.referred_by}
              onChange={(e) => setForm({ ...form, referred_by: e.target.value, referred_by_name: e.target.value ? form.referred_by_name : "", referred_by_phone: e.target.value ? form.referred_by_phone : "" })}
              data-testid="pe-referred-by"
              className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
            >
              <option value="">— None —</option>
              <option value="NCC Employee">NCC Employee</option>
              <option value="Agency">Agency</option>
              <option value="Contractor">Contractor</option>
              <option value="Source / Others">Source / Others</option>
            </select>
            {form.referred_by && (
              <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid="pe-referred-by-details">
                <div>
                  <Label>Referrer Name</Label>
                  <input
                    value={form.referred_by_name}
                    onChange={(e) => setForm({ ...form, referred_by_name: e.target.value })}
                    data-testid="pe-referred-by-name"
                    placeholder="Full name of referrer"
                    className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                  />
                </div>
                <div>
                  <Label>Referrer Number</Label>
                  <input
                    value={form.referred_by_phone}
                    onChange={(e) => setForm({ ...form, referred_by_phone: e.target.value })}
                    data-testid="pe-referred-by-phone"
                    placeholder="10-digit mobile"
                    inputMode="tel"
                    className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="mt-4">
            <Label>About / Bio</Label>
            <textarea rows={4} value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="Brief description of your role and experience"
              data-testid="pe-bio"
              className="w-full resize-y border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none" />
          </div>
        </Section>

        <Section title="Photo & Documents">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Profile Photo</Label>
              <FileUpload accept="image/*" allowCamera onUploaded={(f) => setForm({ ...form, photo: f })} value={form.photo} label="Upload photo" testid="pe-photo" />
            </div>
            <div>
              <Label>ID / License</Label>
              <FileUpload multiple accept="image/*,.pdf" onUploaded={(arr) => setForm({ ...form, id_docs: arr })} value={form.id_docs} label="Upload IDs" testid="pe-id-docs" />
            </div>
            <div>
              <Label>Certifications</Label>
              <FileUpload multiple accept="image/*,.pdf" onUploaded={(arr) => setForm({ ...form, certifications: arr })} value={form.certifications} label="Upload certs" testid="pe-certs" />
            </div>
          </div>
        </Section>

        {error && <div className="border-2 border-red-500 bg-red-50 px-4 py-3 font-mono text-sm text-red-700" data-testid="pe-error">{error}</div>}

        <div className="flex items-center justify-end gap-3">
          <Link to="/" className="border-2 border-slate-900 bg-white px-5 py-3 industrial-label">Cancel</Link>
          <button type="submit" disabled={submitting || hasDuplicates}
            data-testid="pe-submit-btn"
            title={hasDuplicates ? "Resolve duplicate fields before submitting" : undefined}
            className="flex items-center gap-2 border-2 border-slate-900 bg-safety-500 px-6 py-3 industrial-label text-slate-900 transition-all hover:-translate-y-0.5 hover:shadow-brutal disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? "Submitting" : hasDuplicates ? "Resolve duplicates above" : "Submit for Approval"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-2 border-slate-900 bg-white p-5 shadow-brutal-sm">
      <div className="industrial-label border-b-2 border-slate-900 pb-2 text-slate-900">{title}</div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid gap-4 md:grid-cols-2">{children}</div>;
}

function Label({ children }) {
  return <div className="industrial-label mb-1 text-slate-900">{children}</div>;
}

function TField({ label, value, onChange, type = "text", required, testid, status, statusMessage }) {
  const borderColor =
    status === "taken" ? "border-red-500 focus:border-red-600"
    : status === "available" ? "border-emerald-500 focus:border-emerald-600"
    : "border-slate-300 focus:border-slate-900";
  return (
    <label className="block">
      <Label>{label}{required && <span className="text-safety-600"> *</span>}</Label>
      <div className="relative">
        <input
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={`w-full border-2 ${borderColor} bg-slate-50 px-3 py-2 pr-9 font-mono text-sm focus:outline-none`}
        />
        {status === "checking" && (
          <Loader2 className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" data-testid={`${testid}-checking`} />
        )}
        {status === "available" && (
          <CheckCircle2 className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" data-testid={`${testid}-ok`} />
        )}
        {status === "taken" && (
          <XCircle className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-red-500" data-testid={`${testid}-taken`} />
        )}
      </div>
      {status === "taken" && (
        <div className="mt-1 font-mono text-xs text-red-600" data-testid={`${testid}-taken-msg`}>
          {statusMessage || "Already registered. Please use a different one."}
        </div>
      )}
    </label>
  );
}
